<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 v-if="organizationUnitId" :tid="organizationUnitId"></AdminNavigationV2>
        </div>

        <button @click="router.go(-1)" type="button" class="btn btn-lg btn-link" style="margin-left: 20px;">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
            Back to service account
        </button>

        <div style="min-width: 700px; width: 60vw; margin: auto; margin-top: 20px;">
            <h2 class="fg-color-blue">
                API Access Tokens
                <span v-if="targetUser && targetUser.IsServiceUser" class="badge badge-info ml-2">Service Account</span>
            </h2>
            <p v-if="targetUser" class="text-muted">
                Managing access tokens for: <strong>{{targetUser.DisplayName}}</strong> ({{targetUser.Email}})
            </p>
            <hr /><br />

            <div v-if="targetUser && !targetUser.IsServiceUser" class="alert alert-warning" role="alert">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <strong>Warning:</strong> This account is not a service account. Access tokens should primarily be used for service accounts.
            </div>

            <div v-if="succeedUserAccessTokensQueryV1 && targetUser" class="border-box">
                <div class="alert alert-info mb-4" role="alert">
                    <i class="fa fa-info-circle" aria-hidden="true"></i>
                    <strong>About API Access Tokens:</strong> 
                    These tokens are used for API authentication and should be kept secure. They expire after 180 days and can be revoked at any time.
                    <br/><br/>
                    <strong>Important:</strong> The full token is only shown once when generated. Make sure to copy it immediately.
                </div>

                <h4 v-if="accessTokens.length==0" class="text-muted">No access tokens generated</h4>

                <div v-if="accessTokens.length > 0">
                    <div class="row font-weight-bold mb-2">
                        <div class="col-3"><strong>Name</strong></div>
                        <div class="col-2"><strong>Status</strong></div>
                        <div class="col-3"><strong>Expiration</strong></div>
                        <div class="col-3"><strong>Token</strong></div>
                        <div class="col-1"><strong>Actions</strong></div>
                    </div>
                    <hr />

                    <div v-for="token in accessTokens" :key="token.Id" class="row mb-2 align-items-center">
                        <div class="col-3">
                            <span v-if="token.IsExpired || token.IsRevoked" style="text-decoration: line-through;" class="text-muted">
                                {{token.Name}}
                            </span>
                            <span v-else>{{token.Name}}</span>
                        </div>
                        <div class="col-2">
                            <span v-if="token.IsRevoked" class="badge badge-danger">Revoked</span>
                            <span v-else-if="token.IsExpired" class="badge badge-warning">Expired</span>
                            <span v-else class="badge badge-success">Active</span>
                        </div>
                        <div class="col-3">
                            <small :class="{'text-danger': token.IsExpired}">{{token.ExpireAt}}</small>
                        </div>
                        <div class="col-3">
                            <div v-if="token.Id==id" class="input-group input-group-sm">
                                <input :value="copied ? 'COPIED!' : accessToken" type="text" class="form-control bg-white" :class="{'text-success': copied}" readonly>
                                <div class="input-group-append">
                                    <button @click="handleCopyToClipboard" class="btn btn-outline-secondary" type="button" title="Copy to clipboard">
                                        <span class="fa fa-copy"></span>
                                    </button>
                                </div>
                            </div>
                            <span v-else class="text-muted small">
                                <i class="fa fa-lock"></i> Hidden
                            </span>
                        </div>
                        <div class="col-1">
                            <button 
                                v-if="!token.IsRevoked && !token.IsExpired" 
                                @click="handleRevokeAccessTokenCommandV1(token)" 
                                class="btn btn-sm btn-danger" 
                                type="button" 
                                title="Revoke access token">
                                <i class="fa-solid fa-ban"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <hr />
                <button type="button" class="btn btn-primary" @click="handleGenerateAccessTokenCommandV1">
                    <i class="fa fa-key" aria-hidden="true"></i>
                    Generate new access token
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, watch, ref } from 'vue'
    import { guid } from '@common/Guid'
    import { copyToClipboard } from '@common/Common'
    import { useRouter, useRoute } from 'vue-router'
    import { useMyAccessTokensPage } from '@Organizations/AccessTokens/pages-core'
    import { useUserQueryV1 } from '@Organizations/Users/queries-vue-composables'
    import AdminNavigationV2 from "@common/AdminNavigationV2.vue";

    document.title = "Manage Access Tokens"

    const router = useRouter()
    const route = useRoute()

    const id = ref()
    const secret = ref()
    const copied = ref(false)

    // Get user ID from route parameter
    const targetUserId = computed(() => route.params.id as string)

    const {
        useCurrentUserQuery,
        useUserAccessTokensQuery,
        useGenerateAccessTokenCommand,
        useRevokeAccessTokenCommand,
    } = useMyAccessTokensPage()

    const accessToken = computed(() => btoa(`${id.value}/${secret.value}`))

    // Get current admin user info
    const { modelCurrentUserQueryV1: currentUserModel, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const organizationUnitId = computed(() => currentUserModel.value?.OrganizationUnitId)

    // Get target user info (the service account we're managing)
    const { modelUserQueryV1, runUserQueryV1 } = useUserQueryV1(targetUserId)
    const targetUser = computed(() => modelUserQueryV1.value)

    // Get access tokens for the target user
    const { modelUserAccessTokensQueryV1, runUserAccessTokensQueryV1, succeed: succeedUserAccessTokensQueryV1 } = useUserAccessTokensQuery(targetUserId)
    const accessTokens = computed(() => modelUserAccessTokensQueryV1.value?.AccessTokens || [])

    const { invokeGenerateAccessTokenCommandV1, succeedGenerateAccessTokenCommandV1 } = useGenerateAccessTokenCommand(id, organizationUnitId, targetUserId, secret)

    const { invokeRevokeAccessTokenCommandV1, succeedRevokeAccessTokenCommandV1 } = useRevokeAccessTokenCommand(null, targetUserId)

    onMounted(() => {
        runCurrentUserQueryV1()
        if (targetUserId.value) {
            runUserQueryV1()
            runUserAccessTokensQueryV1()
        }
    })

    watch(targetUserId, () => {
        if (targetUserId.value) {
            runUserQueryV1()
            runUserAccessTokensQueryV1()
        }
    })

    watch(succeedGenerateAccessTokenCommandV1, () => runUserAccessTokensQueryV1())
    watch(succeedRevokeAccessTokenCommandV1, () => runUserAccessTokensQueryV1())

    function handleGenerateAccessTokenCommandV1() {
        id.value = guid()
        secret.value = `${guid()}${guid()}`.replace(/-/g, '')
        invokeGenerateAccessTokenCommandV1()
    }

    function handleRevokeAccessTokenCommandV1(token) {
        if (confirm(`Do you really want to revoke access token "${token.Name}"? This action cannot be undone.`)){
            invokeRevokeAccessTokenCommandV1({id: token.Id})
        }
    }

    function handleCopyToClipboard() {
        copyToClipboard(accessToken.value)
        copied.value = true

        const h = setTimeout(() => {
            copied.value = false
            clearTimeout(h)
        }, 2000)
    }
</script>

<style scoped>
    .border-box {
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 24px;
        background-color: #fafafa;
    }
</style>
